import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Link,
  Image,
} from "@react-pdf/renderer";
import logo from "./Main_Logo_AI_Colored_Padding.png";

// Footer metrics (keep these in sync with footer style)
const FOOTER_HEIGHT = 24;
const FOOTER_MARGIN = 16;

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingHorizontal: 20,
    // ✅ reserve room so content never sits under the fixed footer
    paddingBottom: FOOTER_HEIGHT + FOOTER_MARGIN + 20, // ~60
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: "bold",
    textAlign: "center",
    color: "rgb(0, 145, 70)",
  },
  section: { marginBottom: 10, paddingBottom: 5, borderBottom: "1px solid #ddd" },
  label: { fontWeight: "bold", fontSize: 14, marginBottom: 4 },
  text: { fontSize: 12, marginBottom: 5 },
  chipContainer: { flexDirection: "row", flexWrap: "wrap", gap: 5, marginBottom: 10 },
  riskChip: { fontWeight: "bold", fontSize: 12, marginBottom: 5, borderRadius: 5 },
  riskBox: { marginBottom: 5, padding: 5, borderRadius: 4 },
  high: { backgroundColor: "#ff4d4d", color: "#fff", padding: 5 },
  medium: { backgroundColor: "#ffa500", color: "#fff", padding: 5 },
  low: { backgroundColor: "#ffff00", color: "#000", padding: 5 },
  informational: { backgroundColor: "#ccc", color: "#000", padding: 5 },

  // ✅ Footer
  footer: {
    position: "absolute",
    bottom: FOOTER_MARGIN,
    left: 20,
    right: 20,
    height: FOOTER_HEIGHT,
    borderTopWidth: 1,
    borderTopColor: "#ccc",
    paddingTop: 6,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLogo: { width: 60 },
});

// Strip HTML tags from descriptions/solutions
const stripHtml = (html) => html?.replace(/<\/?[^>]+(>|$)/g, "") || "";

// Footer component
const Footer = () => {
  const dateText = new Date().toLocaleDateString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text>{`Generated by Portend AI • Confidential • ${dateText}`}</Text>
      <Image style={styles.footerLogo} src={logo} />
    </View>
  );
};

const PentestReportPDF = ({ data, pageTitle }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Title */}
      <Text style={styles.header}>{pageTitle}</Text>

      {/* Target */}
      <View style={styles.section}>
        <Text style={styles.label}>
          Target: <Text style={styles.text}>{data?.result?.url}</Text>
        </Text>
      </View>

      {/* Report Date */}
      <View style={styles.section}>
        <Text style={styles.label}>Report Date:</Text>
        <Text style={styles.text}>{new Date(data?.date).toLocaleString()}</Text>
      </View>

      {/* Risk Breakdown */}
      <View style={styles.section}>
        <Text style={styles.label}>Risk Breakdown:</Text>
        <View style={styles.chipContainer}>
          {Object.entries(data.result.priorityCount || {}).map(([priority, count]) => (
            <Text key={priority} style={[styles.riskChip, styles[priority.toLowerCase()]]}>
              {priority.charAt(0).toUpperCase() + priority.slice(1)}: {count}
            </Text>
          ))}
        </View>
      </View>

      {/* Vulnerabilities */}
      <View style={styles.section}>
        <Text style={styles.label}>Identified Vulnerabilities:</Text>

        {(data.result.vulnerabilities || []).map((vuln, index) => (
          // Let long items break naturally to the next page; keep styling minimal to avoid layout glitches
          <View key={index} style={{ marginBottom: 10 }}>
            <Text style={[styles.riskBox, styles[vuln.priority.toLowerCase()]]}>
              {vuln.risk} ({vuln.priority})
            </Text>

            <Text style={styles.label}>Description:</Text>
            <Text style={styles.text}>{stripHtml(vuln.desc)}</Text>

            <Text style={styles.label}>Solution:</Text>
            <Text style={styles.text}>{stripHtml(vuln.solution)}</Text>

            <Text style={styles.label}>Affected Locations:</Text>
            {(vuln.locations || [])
              .slice(0, 4) // show only first 4
              .map((loc, i) => (
                <Link key={i} src={loc} style={styles.text}>
                  {loc}
                </Link>
              ))}
          </View>
        ))}
      </View>

      {/* Wapiti Vulnerabilities */}
      <View style={styles.section}>
        <Text style={styles.label}>Wapiti Vulnerabilities:</Text>
        {(data.result.wapitiVulnerabilities || []).map((vuln, index) => (
          <View key={index} style={{ marginBottom: 8 }}>
            <Text style={styles.text}>
              {vuln.risk} — Path: {vuln.path}
            </Text>
          </View>
        ))}
      </View>

      {/* ✅ Spacer to guarantee no overlap with the fixed footer */}
      <View style={{ height: FOOTER_HEIGHT + FOOTER_MARGIN }} />

      {/* ✅ Fixed Footer */}
      <Footer />
    </Page>
  </Document>
);

export default PentestReportPDF;
